import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import type { Project } from "../api/types";
import { Icon } from "../components/icons";
import { toast } from "../components/toast";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { DuplicateProjectDialog } from "../components/DuplicateProjectDialog";
import { ProjectStatusPill } from "../components/ProjectStatusPill";
import { relTime } from "../lib/format";
import { projectHref, workflowByKey } from "../lib/workflows";
import { backdropClose } from "../lib/backdrop";

type View = "tiles" | "list";
type Tab = "active" | "archived";
const VIEW_KEY = "mockingbird.projectView";

// On the list there's no review tally (it lives in per-browser localStorage),
// so the derived status tops out at "Proofed" + badges — accurate without
// loading each project's report.
function StatusPill({ p }: { p: Project }) {
  return <ProjectStatusPill p={p} />;
}

// A toggle that shows — and controls — whether this project's Pickups Package is
// live on a public link. Flipping it enables/revokes the share straight from the
// projects page (optimistic, so it feels instant).
function ShareToggle({ p }: { p: Project }) {
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: (enable: boolean) =>
      api(`/api/modules/proofing/projects/${p.id}/pickups/share`, { method: enable ? "POST" : "DELETE" }),
    onMutate: async (enable: boolean) => {
      await qc.cancelQueries({ queryKey: ["projects"] });
      const prev = qc.getQueriesData<Project[]>({ queryKey: ["projects"] });
      qc.setQueriesData<Project[]>({ queryKey: ["projects"] }, (old) =>
        old?.map((x) => (x.id === p.id ? { ...x, share_enabled: enable } : x))
      );
      return { prev };
    },
    onError: (e: any, _enable, ctx) => {
      ctx?.prev?.forEach(([key, data]) => qc.setQueryData(key, data));
      toast(e?.message || "Couldn't update sharing.", "error");
    },
    onSuccess: (_d, enable) =>
      toast(enable ? "Sharing on — copy the link from the Pickups Package." : "Sharing stopped.", "info"),
    onSettled: () => qc.invalidateQueries({ queryKey: ["projects"] }),
  });
  return (
    <button
      type="button"
      role="switch"
      aria-checked={p.share_enabled}
      className={`share-toggle${p.share_enabled ? " on" : ""}`}
      title={p.share_enabled
        ? "Pickups Package is shared via a public link — click to stop sharing"
        : "Share the Pickups Package via a public link"}
      disabled={mut.isPending}
      onClick={(e) => { e.stopPropagation(); mut.mutate(!p.share_enabled); }}
    >
      <span className="share-toggle-track"><span className="share-toggle-knob" /></span>
      <span className="share-toggle-label">{p.share_enabled ? "Shared" : "Share"}</span>
    </button>
  );
}

function metaLine(p: Project): string {
  const parts = [`${p.n_chapters || p.n_audio} ${p.n_chapters ? "chapters" : "audio files"}`];
  if (p.has_manuscript) parts.push("manuscript ✓");
  parts.push(relTime(p.updated_at));
  return parts.join(" · ");
}

function ProjectMenu({
  archived,
  onRename,
  onDuplicate,
  onArchive,
  onRestore,
  onDelete,
}: {
  archived: boolean;
  onRename: () => void;
  onDuplicate: () => void;
  onArchive: () => void;
  onRestore: () => void;
  onDelete: () => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="proj-menu" onClick={(e) => e.stopPropagation()}>
      <button
        className="icon-btn"
        onClick={() => setOpen((o) => !o)}
        aria-label="Project actions"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Icon name="more" size={18} />
      </button>
      {open && (
        <>
          <div className="menu-backdrop" onClick={() => setOpen(false)} />
          <div className="menu-pop" role="menu">
            <button role="menuitem" onClick={() => { setOpen(false); onRename(); }}>
              <Icon name="edit" size={15} /> Rename…
            </button>
            <button role="menuitem" onClick={() => { setOpen(false); onDuplicate(); }}>
              <Icon name="copy" size={15} /> Duplicate…
            </button>
            {archived ? (
              <button role="menuitem" onClick={() => { setOpen(false); onRestore(); }}>
                <Icon name="restore" size={15} /> Restore
              </button>
            ) : (
              <button role="menuitem" onClick={() => { setOpen(false); onArchive(); }}>
                <Icon name="archive" size={15} /> Archive
              </button>
            )}
            <button role="menuitem" className="danger" onClick={() => { setOpen(false); onDelete(); }}>
              <Icon name="trash" size={15} /> Delete…
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export function ProjectList({ workflow }: { workflow?: string } = {}) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const wf = workflow ? workflowByKey(workflow) : undefined;
  const [view, setView] = useState<View>(() => (localStorage.getItem(VIEW_KEY) as View) || "tiles");
  const [tab, setTab] = useState<Tab>("active");
  const [confirmDelete, setConfirmDelete] = useState<Project | null>(null);
  const [renameTarget, setRenameTarget] = useState<Project | null>(null);
  const [dupTarget, setDupTarget] = useState<Project | null>(null);
  const [newName, setNewName] = useState("");
  useEffect(() => { localStorage.setItem(VIEW_KEY, view); }, [view]);

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ["projects", tab],
    queryFn: () =>
      api<{ projects: Project[] }>(
        tab === "archived" ? "/api/projects?archived=true" : "/api/projects"
      ).then((r) => r.projects),
    // Cheap endpoint, but no need for second-by-second polling (it also refetches
    // on window focus). Gentle interval keeps the list fresh without churn.
    refetchInterval: 15000,
  });

  // Per-workflow lists are the same data, filtered to projects that workflow
  // has touched (sidebar → Proofing / Spec check). No filter ⇒ all projects.
  const list = data && workflow ? data.filter((p) => (p.workflows as any)?.[workflow]) : data;

  const refresh = () => qc.invalidateQueries({ queryKey: ["projects"] });

  const archiveMut = useMutation({
    mutationFn: (v: { id: string; archived: boolean }) =>
      api(`/api/projects/${v.id}/archive`, { method: "POST", body: JSON.stringify({ archived: v.archived }) }),
    onSuccess: (_d, v) => { refresh(); toast(v.archived ? "Project archived." : "Project restored.", "info"); },
    onError: (e: any) => toast(e.message || "That didn't go through — try again?", "error"),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => api(`/api/projects/${id}`, { method: "DELETE" }),
    onSuccess: () => { refresh(); setConfirmDelete(null); toast("Project deleted.", "info"); },
    onError: (e: any) => { toast(e.message || "Couldn't delete — try again?", "error"); setConfirmDelete(null); },
  });

  const renameMut = useMutation({
    mutationFn: (v: { id: string; name: string }) =>
      api(`/api/projects/${v.id}/rename`, { method: "POST", body: JSON.stringify({ name: v.name }) }),
    onSuccess: (_d, v) => { refresh(); qc.invalidateQueries({ queryKey: ["project", v.id] }); setRenameTarget(null); toast("Project renamed.", "success"); },
    onError: (e: any) => toast(e.message || "Couldn't rename — try again?", "error"),
  });
  function submitRename() {
    const name = newName.trim();
    if (!renameTarget || !name || name === renameTarget.name) return;
    renameMut.mutate({ id: renameTarget.id, name });
  }

  const menuFor = (p: Project) => (
    <ProjectMenu
      archived={p.archived}
      onRename={() => { setNewName(p.name); setRenameTarget(p); }}
      onDuplicate={() => setDupTarget(p)}
      onArchive={() => archiveMut.mutate({ id: p.id, archived: true })}
      onRestore={() => archiveMut.mutate({ id: p.id, archived: false })}
      onDelete={() => setConfirmDelete(p)}
    />
  );

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>{wf ? wf.label : "Projects"}</h1>
          <div className="sub">
            {wf ? wf.listSub : "Line up each audio file with the manuscript and catch every flub."}
          </div>
        </div>
        <div className="row" style={{ gap: 10 }}>
          <div className="seg" role="group" aria-label="Filter">
            <button className={tab === "active" ? "on" : ""} onClick={() => setTab("active")} aria-pressed={tab === "active"}>
              Active
            </button>
            <button className={tab === "archived" ? "on" : ""} onClick={() => setTab("archived")} aria-pressed={tab === "archived"}>
              Archived
            </button>
          </div>
          <div className="view-toggle" role="group" aria-label="View">
            <button className={view === "tiles" ? "on" : ""} onClick={() => setView("tiles")} title="Tiles" aria-pressed={view === "tiles"}>
              <Icon name="grid" size={16} />
            </button>
            <button className={view === "list" ? "on" : ""} onClick={() => setView("list")} title="List" aria-pressed={view === "list"}>
              <Icon name="list" size={16} />
            </button>
          </div>
          <Link to="/new" className="btn primary">
            <Icon name="plus" size={16} /> New project
          </Link>
        </div>
      </div>

      {isLoading && <div className="muted">Loading…</div>}

      {/* Only take over the view when there's nothing to show — a background
          refetch that blips shouldn't wipe an already-visible list. Reassure
          (data isn't lost) and offer a one-click retry. */}
      {error && !list && (
        <div className="empty-state">
          <Icon name="info" size={40} className="muted" />
          <h3>We couldn't reach the server</h3>
          <p>Your projects are safe — the app just couldn't load them right now. This is usually a brief hiccup.</p>
          <button className="btn primary" onClick={() => refetch()} disabled={isFetching}>
            <Icon name="rotate-cw" size={16} /> {isFetching ? "Retrying…" : "Retry"}
          </button>
        </div>
      )}

      {list && list.length === 0 && (
        <div className="empty-state">
          <Icon name={wf ? wf.icon : tab === "archived" ? "archive" : "folder"} size={40} className="muted" />
          {wf ? (
            <>
              <h3>No projects in {wf.label} yet</h3>
              <p>
                {wf.key === "proofing"
                  ? "Open a project and start proofing — it'll appear here."
                  : "Open a project and run a spec check — it'll appear here."}
              </p>
              <Link to="/" className="btn">Browse all projects</Link>
            </>
          ) : tab === "archived" ? (
            <>
              <h3>No archived projects</h3>
              <p>Projects you archive will show up here — and you can restore them any time.</p>
            </>
          ) : (
            <>
              <h3>Nothing here yet</h3>
              <p>Start a project — drop in your manuscript and the audio files, and we'll line them up for you.</p>
              <Link to="/new" className="btn primary">
                <Icon name="plus" size={16} /> New project
              </Link>
            </>
          )}
        </div>
      )}

      {list && list.length > 0 && view === "tiles" && (
        <div className="project-grid">
          {list.map((p) => {
            const pct = p.n_chapters ? Math.round((p.proofed / p.n_chapters) * 100) : 0;
            return (
              <div key={p.id} className="card project-card" onClick={() => navigate(projectHref(p.id, workflow))}>
                <div className="row" style={{ justifyContent: "space-between" }}>
                  <h3>{p.name}</h3>
                  <div className="row" style={{ gap: 6 }}>
                    <StatusPill p={p} />
                    {menuFor(p)}
                  </div>
                </div>
                <div className="meta">
                  <span>{p.n_chapters || p.n_audio} {p.n_chapters ? "chapters" : "audio files"}</span>
                  {p.has_manuscript && <span>· manuscript ✓</span>}
                  <span>· {relTime(p.updated_at)}</span>
                </div>
                {p.n_chapters > 0 && (
                  <div className="bar">
                    <div className="progress-track">
                      <div className="progress-fill" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="muted" style={{ fontSize: 12, marginTop: 5 }}>
                      {p.proofed}/{p.n_chapters} proofed
                    </div>
                  </div>
                )}
                <div className="pc-foot"><ShareToggle p={p} /></div>
              </div>
            );
          })}
        </div>
      )}

      {list && list.length > 0 && view === "list" && (
        <div className="project-list">
          {list.map((p) => {
            const pct = p.n_chapters ? Math.round((p.proofed / p.n_chapters) * 100) : 0;
            return (
              <div key={p.id} className="project-row" onClick={() => navigate(projectHref(p.id, workflow))}>
                <div className="pr-name">
                  <strong>{p.name}</strong>
                  <StatusPill p={p} />
                </div>
                <div className="pr-meta muted">{metaLine(p)}</div>
                <div className="pr-prog">
                  {p.n_chapters > 0 ? (
                    <>
                      <div className="progress-track" style={{ width: 90 }}>
                        <div className="progress-fill" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="muted">{p.proofed}/{p.n_chapters}</span>
                    </>
                  ) : (
                    <span className="muted">—</span>
                  )}
                </div>
                <ShareToggle p={p} />
                {menuFor(p)}
              </div>
            );
          })}
        </div>
      )}

      {renameTarget && (
        <div className="modal-overlay" {...backdropClose(() => { if (!renameMut.isPending) setRenameTarget(null); })}>
          <div className="modal-card" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <h3>Rename project</h3>
            <div className="modal-body">
              <input
                className="input"
                autoFocus
                maxLength={120}
                value={newName}
                placeholder="Project name"
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") { e.preventDefault(); submitRename(); }
                  else if (e.key === "Escape") { e.preventDefault(); setRenameTarget(null); }
                }}
              />
            </div>
            <div className="modal-actions">
              <button className="btn" onClick={() => setRenameTarget(null)} disabled={renameMut.isPending}>Cancel</button>
              <button className="btn primary" onClick={submitRename}
                disabled={renameMut.isPending || !newName.trim() || newName.trim() === renameTarget.name}>
                {renameMut.isPending ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {dupTarget && (
        <DuplicateProjectDialog
          projectId={dupTarget.id}
          projectName={dupTarget.name}
          onClose={() => setDupTarget(null)}
        />
      )}
      <ConfirmDialog
        open={confirmDelete !== null}
        title="Delete this project?"
        danger
        confirmLabel="Delete permanently"
        busy={deleteMut.isPending}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={() => confirmDelete && deleteMut.mutate(confirmDelete.id)}
        body={
          <>
            <p>
              <strong>“{confirmDelete?.name}”</strong> and all of its files — audio, manuscript,
              transcripts and flags — will be permanently deleted. This can't be undone.
            </p>
            <p className="muted" style={{ fontSize: 13 }}>
              Want to keep it but hide it? Use <em>Archive</em> instead.
            </p>
          </>
        }
      />
    </div>
  );
}
